/**
 * DriverSchedulePage — `/driver-portal/schedule`.
 *
 * Live · `GET /v1/routes?driverId=…` — renders today's stops from the most
 * recent route assigned to the signed-in driver. Web is read-only; status
 * transitions (start, arrive, complete, fail) happen on the mobile-driver app.
 */
import { type JSX, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  type LucideIcon,
  Smartphone,
  Truck,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listMyRoutes } from '../../lib/api/driver-portal';
import type { StopStatus } from '../../lib/api/delivery';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { FilterBar } from '../mail/shared/MailUI';
import { useDriverPortalContext } from './DriverPortalLayout';

const STATUS_TONE: Readonly<
  Record<StopStatus, { tone: string; icon: LucideIcon; label: string }>
> = {
  COMPLETED: { tone: 'bg-success-light text-success',   icon: CheckCircle2, label: 'Delivered' },
  ARRIVED:   { tone: 'bg-blue-100 text-blue-800',        icon: Truck,        label: 'Arrived' },
  PENDING:   { tone: 'bg-gray-100 text-gray-700',        icon: Clock,        label: 'Planned' },
  FAILED:    { tone: 'bg-warning-light text-warning',    icon: AlertCircle,  label: 'Failed' },
  SKIPPED:   { tone: 'bg-gray-100 text-gray-600',        icon: AlertCircle,  label: 'Skipped' },
};

export function DriverSchedulePage(): JSX.Element {
  const { driverId } = useDriverPortalContext();
  const [filter, setFilter] = useState<'ALL' | StopStatus>('ALL');
  const [search, setSearch] = useState<string>('');

  const routes = useQuery({
    queryKey: ['driver-portal', 'routes', driverId],
    queryFn: () => listMyRoutes({ driverId }, 5),
    retry: false,
    staleTime: 30_000,
  });

  const denied =
    routes.isError &&
    routes.error instanceof ApiError &&
    (routes.error.status === 401 || routes.error.status === 403);

  // Pick the most recent route — that's the "today's schedule" the driver
  // expects. Stops are sequenced by the routing engine.
  const activeRoute = routes.data?.items[0] ?? null;
  const stops = activeRoute?.stops ?? [];

  const filtered = useMemo(() => {
    return stops.filter((s) => {
      if (filter !== 'ALL' && s.status !== filter) return false;
      if (search.trim().length === 0) return true;
      const needle = search.toLowerCase();
      const addr =
        typeof s.address === 'string'
          ? s.address
          : JSON.stringify(s.address ?? '');
      return (
        (s.contactName ?? '').toLowerCase().includes(needle) ||
        addr.toLowerCase().includes(needle) ||
        (s.mailItemId ?? '').toLowerCase().includes(needle)
      );
    });
  }, [stops, filter, search]);

  const completed = stops.filter((s) => s.status === 'COMPLETED').length;
  const failed = stops.filter((s) => s.status === 'FAILED').length;
  const remaining = stops.filter(
    (s) => s.status === 'PENDING' || s.status === 'ARRIVED',
  ).length;

  return (
    <div>
      <PageHeader
        title="Today's schedule"
        description="Engine-sequenced. Read-only on web — actions happen on the mobile app."
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Tile label="Total stops" value={stops.length} />
        <Tile label="Delivered" value={completed} tone="text-success" />
        <Tile label="Remaining" value={remaining} />
        <Tile label="Failed" value={failed} tone="text-warning" />
      </div>

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search recipient, address or tracking number"
      >
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as 'ALL' | StopStatus)}
          aria-label="Status filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          <option value="ALL">All statuses</option>
          <option value="PENDING">Planned</option>
          <option value="ARRIVED">Arrived</option>
          <option value="COMPLETED">Delivered</option>
          <option value="FAILED">Failed</option>
          <option value="SKIPPED">Skipped</option>
        </select>
      </FilterBar>

      {routes.isPending ? (
        <p className="text-sm text-muted">Loading schedule…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry{' '}
          <code className="font-mono">Delivery/readRoute</code>.
        </DeferredNotice>
      ) : routes.isError ? (
        <DeferredNotice title="Service unreachable">
          {(routes.error as Error).message}
        </DeferredNotice>
      ) : !activeRoute ? (
        <p className="text-sm text-muted bg-white border border-border rounded-xl p-8 text-center">
          No route assigned to you yet.
        </p>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold w-12">
                  #
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Status
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Recipient · Address
                </th>
                <th className="text-left px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Kind
                </th>
                <th className="text-right px-4 py-2 text-[11px] uppercase tracking-widest text-muted font-semibold">
                  Tracking
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((s) => {
                const conf = STATUS_TONE[s.status];
                const Icon = conf.icon;
                const addr =
                  typeof s.address === 'string'
                    ? s.address
                    : (s.address as { line1?: string } | null)?.line1 ?? '—';
                return (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-bold text-gray-900">
                      {s.sequence}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded ${conf.tone}`}
                      >
                        <Icon className="w-3 h-3" /> {conf.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">
                        {s.contactName ?? '—'}
                      </p>
                      <p className="text-xs text-muted">{addr}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {s.kind}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-[10px] font-mono text-muted">
                      {s.mailItemId ?? '—'}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-muted text-sm"
                  >
                    No stops match the filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900">
        <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          To start navigation, mark a stop as delivered, capture POD, or
          trigger re-route — open the NIPOST Driver app on your phone.
        </span>
      </div>
    </div>
  );
}

function Tile(props: {
  readonly label: string;
  readonly value: number;
  readonly tone?: string;
}): JSX.Element {
  return (
    <div className="bg-white border border-border rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-widest text-muted font-semibold">
        {props.label}
      </p>
      <p
        className={`text-2xl font-extrabold mt-0.5 ${
          props.tone ?? 'text-gray-900'
        }`}
      >
        {props.value}
      </p>
    </div>
  );
}
